import useRequest from "@/lib/request/useRequest";
import {
    Button,
    Divider,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    Selection,
    SelectItem,
    Textarea,
    Chip,
} from "@heroui/react";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApplyNChip from "../common/ApplyNChip";
import DoNotApplyNChip from "../common/DoNotApplyNChip";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import { FieldRecommendation } from "@/types/field";
import IrrigateChip from "../common/IrrigateChip";
import DoNotIrrigateChip from "../common/DoNotIrrigateChip";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

function NoChangeChip() {
    return (
        <Chip radius="sm" size="sm"
            classNames={{
                base: "bg-gray-100",
                content: "text-gray-900 font-bold",
            }}>
            No Change
        </Chip>
    );
};

export type UserRecommendationTarget = {
    field: string | number;
    crop_zone?: number | null;
};

function UserRecommendationForm({
    targets,
    onClose,
    platformRecommendationContent,
    recommendation,
}: {
    targets: UserRecommendationTarget[];
    onClose: (refetch: boolean) => void;
    platformRecommendationContent?: ReactNode;
    recommendation?: FieldRecommendation;
}) {
    const { useMutateRequest } = useRequest();
    const { t } = useTranslation();
    const { fieldHasIrrigation } = useFieldDashboard() ?? {};

    const [recommendApplication, setRecommendApplication] = useState<Selection>(
        new Set(["null"]),
    );
    const [recommendIrrigation, setRecommendIrrigation] = useState<Selection>(
        new Set(["null"]),
    );
    const [notes, setNotes] = useState<string | null>(null);
    const [daysActive, setDaysActive] = useState<number | null>(5);

    useEffect(() => {
        if (recommendation?.user_recommendation) {
            const edit = recommendation.user_recommendation;
            setNotes(edit.notes);
            setRecommendApplication(
                new Set([edit.recommend_application?.toString() ?? "null"]),
            );
            setRecommendIrrigation(
                new Set([edit.recommend_irrigation?.toString() ?? "null"]),
            );
            setDaysActive(edit.days_active ?? 5);
        }
    }, [recommendation?.user_recommendation]);

    const editRecommendationRequest = useMutateRequest({
        method: "put",
        errorMessage: t("error.failed_to_edit_recommendation"),
        successMessage: t("success.edited_recommendation"),
    });

    const saveRecommendationEdit = useCallback(() => {
        editRecommendationRequest.mutate(
            {
                url:
                    targets.length === 1
                        ? `user_recommendations/upsert/`
                        : `user_recommendations/bulk_upsert/`,
                data:
                    targets.length === 1
                        ? {
                            notes,
                            field: targets[0].field,
                            crop_zone: targets[0].crop_zone ?? null,
                            recommendation_date:
                                getTodayWithTimeTravel().format("YYYY-MM-DD"),
                            days_active: daysActive ?? 5,
                            recommend_application:
                                recommendApplication != "all"
                                    ? recommendApplication.has("true")
                                        ? true
                                        : recommendApplication.has("false")
                                            ? false
                                            : null
                                    : null,
                            recommend_irrigation:
                                recommendIrrigation != "all"
                                    ? recommendIrrigation.has("true")
                                        ? true
                                        : recommendIrrigation.has("false")
                                            ? false
                                            : null
                                    : null,
                        }
                        : {
                            targets: targets.map((target) => ({
                                field: target.field,
                                crop_zone: target.crop_zone ?? null,
                            })),
                            notes,
                            recommendation_date:
                                getTodayWithTimeTravel().format("YYYY-MM-DD"),
                            days_active: daysActive ?? 5,
                            recommend_application:
                                recommendApplication != "all"
                                    ? recommendApplication.has("true")
                                        ? true
                                        : recommendApplication.has("false")
                                            ? false
                                            : null
                                    : null,
                            recommend_irrigation:
                                recommendIrrigation != "all"
                                    ? recommendIrrigation.has("true")
                                        ? true
                                        : recommendIrrigation.has("false")
                                            ? false
                                            : null
                                    : null,
                        },
            },
            {
                onSuccess: () => {
                    onClose(true);
                },
            },
        );
    }, [
        editRecommendationRequest,
        notes,
        targets,
        daysActive,
        recommendApplication,
        recommendIrrigation,
        onClose,
    ]);

    return (
        <Modal isOpen onClose={() => onClose(false)}>
            <ModalContent>
                <ModalHeader>Edit Recommendation</ModalHeader>
                <ModalBody>
                    {platformRecommendationContent && (
                        <>
                            <div className="flex flex-row gap-2 items-center">
                                <div className="font-bold">
                                    Platform Recommendation:
                                </div>
                                <div>{platformRecommendationContent}</div>
                            </div>
                            <Divider />
                        </>
                    )}
                    <div className="flex flex-col gap-2 items-center">
                        <Select
                            label="New N Recommendation"
                            placeholder=" "
                            selectedKeys={recommendApplication}
                            onSelectionChange={setRecommendApplication}
                            classNames={{
                                base: "min-w-[200px]",
                            }}
                            size="sm"
                            labelPlacement="outside"
                            renderValue={(items) => {
                                return items.map((item) => {
                                    if (item.key == "null") {
                                        return <NoChangeChip />;
                                    }
                                    if (item.key === "true") {
                                        return <ApplyNChip />;
                                    }
                                    return <DoNotApplyNChip />;
                                });
                            }}
                        >
                            <SelectItem key={null}>
                                <NoChangeChip />
                            </SelectItem>
                            <SelectItem key={"true"}>
                                <ApplyNChip />
                            </SelectItem>
                            <SelectItem key={"false"}>
                                <DoNotApplyNChip />
                            </SelectItem>
                        </Select>
                        {fieldHasIrrigation && (
                            <Select
                                label="New Irrigation Recommendation"
                                placeholder=" "
                                selectedKeys={recommendIrrigation}
                                onSelectionChange={setRecommendIrrigation}
                                classNames={{
                                    base: "min-w-[200px]",
                                }}
                                size="sm"
                                labelPlacement="outside"
                                renderValue={(items) => {
                                    return items.map((item) => {
                                        if (item.key == "null") {
                                            return <NoChangeChip />;
                                        }
                                        if (item.key === "true") {
                                            return <IrrigateChip />;
                                        }
                                        return <DoNotIrrigateChip />;
                                    });
                                }}
                            >
                                <SelectItem key={null}>
                                    <NoChangeChip />
                                </SelectItem>
                                <SelectItem key={"true"}>
                                    <IrrigateChip />
                                </SelectItem>
                                <SelectItem key={"false"}>
                                    <DoNotIrrigateChip />
                                </SelectItem>
                            </Select>
                        )}
                        <Input
                            type="number"
                            label="Days Active"
                            value={daysActive?.toString() ?? ""}
                            onValueChange={(value) =>
                                setDaysActive(Number(value))
                            }
                            labelPlacement="outside"
                            max={7}
                            min={1}
                            size="sm"
                            endContent={<span className="text-xs">Days</span>}
                            classNames={{
                                description: "text-xs text-gray-500",
                            }}
                            description={
                                daysActive &&
                                `Recommendation will end on ${getTodayWithTimeTravel().add(daysActive, "day").format("MM/DD/YYYY")} `
                            }
                        />
                    </div>
                    <Textarea
                        labelPlacement="outside"
                        label="Notes"
                        size="sm"
                        minRows={4}
                        value={notes ?? ""}
                        onValueChange={setNotes}
                    />
                </ModalBody>
                <ModalFooter className="flex flex-row justify-end">
                    <Button
                        onPress={() => onClose(false)}
                        isDisabled={editRecommendationRequest.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onPress={saveRecommendationEdit}
                        isLoading={editRecommendationRequest.isPending}
                        isDisabled={editRecommendationRequest.isPending}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default UserRecommendationForm;
